import React, { useCallback, useState } from 'react';
import { Dialog, Typography, Stack } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';
import { ReactComponent as DocUploadIcon } from '../../../../../assets/icons/document-upload.svg';
import { palette } from '../../../../../theme/default';
import MButton from '../../../../MUI/Button/MButton';
import Table from '../../../../atoms/Table/Table';
import {
  documentUploadsTableColumns,
  FIVE_MEGABYTES_IN_BYTES,
} from './DocumentTemplateHistory.constants';

const DialogStyles = {
  '& .MuiDialog-container': {
    '& .MuiPaper-root': {
      width: '100%',
      maxWidth: '784px',
      padding: '32px',
      borderRadius: '24px',
    },
  },
};

const DocumentTemplateHistoryUpload = ({ title, isOpen, onConfirm, handleClose }) => {
  const [filetoUpload, setFileToUpload] = useState(null);
  const [addedFile, setAddedFile] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();

      reader.onload = () => {
        setFileToUpload([
          {
            documentName: file.name,
            isFileMoreThanFiveMb: file.size > FIVE_MEGABYTES_IN_BYTES,
            isDocxFormat: file.path.endsWith('.docx'),
          },
        ]);

        setAddedFile(file);
      };
      reader.readAsArrayBuffer(file);
    }, []);
  });

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
  });

  const onClose = () => {
    handleClose();
    setAddedFile(null);
    setFileToUpload(null);
  };

  const handleUpload = () => {
    handleClose();
    onConfirm(addedFile, filetoUpload);
    setFileToUpload(null);
  };

  const handleDeleteRow = () => {
    setFileToUpload(null);
  };

  return (
    <Dialog
      fullWidth
      open={isOpen}
      sx={DialogStyles}
      onClose={onClose}
      data-testid="document_template_upload_dialog"
    >
      <Typography variant="h4" data-testid="document_template_upload_dialog_title">
        {title}
      </Typography>
      {filetoUpload ? (
        <Stack mt={4} mb={4}>
          <Table
            columns={documentUploadsTableColumns}
            rows={filetoUpload}
            onDeleteRow={handleDeleteRow}
          />
        </Stack>
      ) : (
        <Stack {...getRootProps()} mt={4} mb={4}>
          <input data-testid="document_template_upload_input" {...getInputProps()} />
          <Stack
            display="flex"
            justifyContent="center"
            alignItems="center"
            pt="50px"
            pb="48px"
            sx={{
              backgroundColor: palette.upload.dragAndDropArea,
              border: 'dashed 1px solid grey',
              borderRadius: '24px',
            }}
          >
            <DocUploadIcon />
            <Typography
              color={palette.text.secondary}
              fontSize="18px"
              fontWeight={500}
              mb={1}
              mt={1}
            >
              Drop your documents here, or select
            </Typography>
            <Typography
              color={palette.text.browseFile}
              fontSize="18px"
              fontWeight={400}
              sx={{
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              Click to browse
            </Typography>
          </Stack>
        </Stack>
      )}

      <Stack display="flex" flexDirection="row" justifyContent="flex-end" height="48px" gap={2}>
        <MButton
          variant="bordered"
          sx={{ width: '104px', fontSize: '16px' }}
          onClick={onClose}
          data-testid="document_template_upload_cancel_btn"
        >
          Cancel
        </MButton>
        <MButton
          sx={{ width: '104px', fontSize: '16px' }}
          onClick={handleUpload}
          disabled={!filetoUpload?.length}
          data-testid="document_template_upload_btn"
        >
          Upload
        </MButton>
      </Stack>
    </Dialog>
  );
};

DocumentTemplateHistoryUpload.propTypes = {
  title: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  handleClose: PropTypes.bool.isRequired,
};

export default DocumentTemplateHistoryUpload;
